import { useMutation } from '@apollo/client'
import Hls from 'hls.js'
import throttle from 'lodash.throttle'
import { Options } from 'plyr'
import { APITypes } from 'plyr-react'
import 'plyr-react/plyr.css'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { UpdateViwewingTimeDocument } from '~/__generated__/graphql'
import { Plyr } from '~/components/atoms/Plyr'
import { useVideoLanguage } from '~/hooks/useGetVideoLanguage'
import browserEnv from '~/libs/env/browserEnv'
import * as GA from '~/libs/ga'
import { getVideoWatchedPercent } from '~/libs/video/getVideoWatchedPercent'
import { useVideoDetail } from '../../hooks/useVideoDetail'
import { useVideoEventHandler } from '../VideoEventHandlerProvider'

const Player: FC<{ id: string }> = ({ id }) => {
  const { data, cookie, refetch } = useVideoDetail(id)
  const [updateViewingTime] = useMutation(UpdateViwewingTimeDocument, {
    ignoreResults: true,
  })
  const video = data?.originalVideo
  const videoElemId = 'player-video'
  const lambda = browserEnv.NEXT_PUBLIC_PLAYER_PROXY_LAMBDA

  const ref = useRef<APITypes>(null)
  const hls = useMemo(() => new Hls(), [])
  const isHlsSupported = useMemo(() => Hls.isSupported(), [])
  const hasQuality = useRef<boolean>(false)

  const isReady = useRef(false)
  const timeUpdateEvents = useRef<Set<number>>(new Set())
  const lapsedSec = useRef<number>(0)

  const didSendWatchComplete = useRef(false)

  const sendVideoInsightsEvent = useCallback(
    (action: string, value?: string) => {
      if (isReady.current) {
        GA.sendEvent({
          event: 'video_insights',
          screen: 'movie_page',
          category: 'player',
          action,
          label: `${video?.id}`,
          value,
        })
      }
    },
    [video?.id],
  )

  const patchViewing = useCallback(
    (event: Event) => {
      if (event.target instanceof HTMLVideoElement) {
        const seconds = event.target.currentTime
        if (seconds == 0) return
        const viewingMin = Math.floor(seconds / 60.0)
        const viewingSecond = Math.floor(seconds % 60.0)
        updateViewingTime({
          variables: {
            videoId: id,
            viewingMin,
            viewingSecond,
          },
        })
      }
    },
    [id, updateViewingTime],
  )
  const patchViewingOnTimeUpdate = useMemo(() => throttle(patchViewing, 10000), [patchViewing])

  const onVolumeChange = useMemo(
    () =>
      throttle((event: Event) => {
        sendVideoInsightsEvent('video_views_volume_change')
      }, 3000),
    [sendVideoInsightsEvent],
  )

  const onTimeUpdate = useMemo(
    () =>
      throttle((event: Event) => {
        if (!isReady.current) return
        if (lapsedSec.current >= 1 && !timeUpdateEvents.current.has(1)) {
          sendVideoInsightsEvent('video_views_time_watched_second')
          timeUpdateEvents.current.add(1)
        }
        if (lapsedSec.current >= 3 && !timeUpdateEvents.current.has(3)) {
          sendVideoInsightsEvent('video_views_3s_watched_count')
          timeUpdateEvents.current.add(3)
        }
        if (lapsedSec.current >= 5 && !timeUpdateEvents.current.has(5)) {
          sendVideoInsightsEvent('video_views_5s_watched_count')
          timeUpdateEvents.current.add(5)
        }
        if (lapsedSec.current >= 10 && !timeUpdateEvents.current.has(10)) {
          sendVideoInsightsEvent('video_views_10s_watched_count')
          timeUpdateEvents.current.add(10)
        }
        if (lapsedSec.current >= 15 && !timeUpdateEvents.current.has(15)) {
          sendVideoInsightsEvent('video_views_15s_watched_count')
          timeUpdateEvents.current.add(15)
        }
        if (lapsedSec.current >= 30 && !timeUpdateEvents.current.has(30)) {
          sendVideoInsightsEvent('video_views_30s_watched_count')
          timeUpdateEvents.current.add(30)
        }
        lapsedSec.current += 1

        const video = event.target as HTMLVideoElement
        if (
          !didSendWatchComplete.current &&
          video != null &&
          video.currentTime / video.duration > 0.9
        ) {
          sendVideoInsightsEvent('video_views_complete_watched_count')
          didSendWatchComplete.current = true
        }
      }, 1000),
    [sendVideoInsightsEvent],
  )

  const onLoadedData = useCallback(
    (event: Event) => {
      if (!(event.target instanceof HTMLVideoElement) || video?.viewingTime == null) {
        return
      }
      const watchedPercent = getVideoWatchedPercent(
        {
          minutes: video?.duration?.minutes ?? undefined,
          seconds: video?.duration?.seconds ?? undefined,
        },
        {
          minutes: video.viewingTime.viewingMin ?? undefined,
          seconds: video.viewingTime.viewingSecond ?? undefined,
        },
      )
      // 95%以上視聴済みの場合は、最初から再生する
      if (watchedPercent && watchedPercent >= 0.95) {
        event.target.currentTime = 0
      } else {
        event.target.currentTime =
          (video?.viewingTime?.viewingMin ?? 0) * 60 + (video?.viewingTime?.viewingSecond ?? 0)
      }
    },
    [video?.viewingTime, video?.duration],
  )

  const { addEventListeners } = useVideoEventHandler({
    onVideoPlay(event) {
      sendVideoInsightsEvent('video_views_play_count')
    },
    onVideoPause(event) {
      sendVideoInsightsEvent('video_views_stop_count')
    },
    onVolumeChange(event) {
      onVolumeChange(event)
    },
    onEnterPip(event) {
      sendVideoInsightsEvent('video_views_pip_on')
    },
    onLeavePip(event) {
      sendVideoInsightsEvent('video_views_pip_off')
    },
    onRateChange(event) {
      const videoElem = event.target
      if (!(videoElem instanceof HTMLVideoElement)) {
        return
      }
      sendVideoInsightsEvent('video_views_speed_value', `${videoElem.playbackRate}`)
    },
    onTimeUpdate(event) {
      patchViewingOnTimeUpdate(event)
      onTimeUpdate(event)
    },
    onLoadedData(event) {
      onLoadedData(event)
    },
    onEnded(event) {
      sendVideoInsightsEvent('video_views_complete_watched_count')
    },
    onCanPlay(event) {
      const videoElem = event.target
      if (!(videoElem instanceof HTMLVideoElement)) {
        return
      }
      if (!videoElem.src.endsWith('blank.mp4')) {
        isReady.current = true
      } else {
        isReady.current = false
      }
    },
  })

  const onPlyrRewind = useCallback(
    (e: Plyr.PlyrEvent) => {
      sendVideoInsightsEvent('video_views_back')
    },
    [sendVideoInsightsEvent],
  )
  const onPlyrFastForward = useCallback(
    (e: Plyr.PlyrEvent) => {
      sendVideoInsightsEvent('video_views_forward')
    },
    [sendVideoInsightsEvent],
  )
  const onPlyrMute = useCallback(
    (e: Plyr.PlyrEvent) => {
      if (ref.current?.plyr.muted != undefined) {
        const muteOff = ref.current?.plyr.muted == true
        sendVideoInsightsEvent(muteOff ? 'video_views_mute_off' : 'video_views_mute_on')
      }
    },
    [sendVideoInsightsEvent],
  )
  const onPlyrAirplay = useCallback(
    (e: Plyr.PlyrEvent) => {
      sendVideoInsightsEvent('video_views_airplay')
    },
    [sendVideoInsightsEvent],
  )
  const onPlyrFullscreen = useCallback(
    (e: Plyr.PlyrEvent) => {
      if (document.fullscreenElement) {
        sendVideoInsightsEvent('video_views_shrink')
      } else {
        sendVideoInsightsEvent('video_views_fullscreen')
      }
    },
    [sendVideoInsightsEvent],
  )
  const onPlyrPlay = useCallback(
    (e: Plyr.PlyrEvent) => {
      if (ref.current?.plyr.ended == true) {
        sendVideoInsightsEvent('video_views_reload')
      }
    },
    [sendVideoInsightsEvent],
  )

  const [plyrOptions, setPlyrOptions] = useState<Options | undefined>({
    muted: false,
    autoplay: true,
    keyboard: {
      global: true,
    },
    tooltips: {
      controls: true,
      seek: true,
    },
    controls: [
      'play-large', // The large play button in the center
      'rewind', // Rewind by the seek time (default 10 seconds)
      'play', // Play/pause playback
      'fast-forward', // Fast forward by the seek time (default 10 seconds)
      'progress', // The progress bar and scrubber for playback and buffering
      'current-time', // The current time of playback
      'duration', // The full duration of the media
      'mute', // Toggle mute
      'volume', // Volume control
      'captions', // Toggle captions
      'settings', // Settings menu
      'pip', // Picture-in-picture (currently Safari only)
      'airplay', // Airplay (currently Safari only)
      'fullscreen', // Toggle fullscreen
    ],
    listeners: {
      rewind: onPlyrRewind,
      fastForward: onPlyrFastForward,
      mute: onPlyrMute,
      airplay: onPlyrAirplay,
      fullscreen: onPlyrFullscreen,
      play: onPlyrPlay,
    },
  })

  const onMediaAttached = useCallback(
    (src: string) => {
      hls.loadSource(src)
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (hasQuality.current) return // early quit if already set

        const levels = hls.levels
        const quality: Options['quality'] = {
          default: levels[levels.length - 1].height,
          options: levels.map((level) => level.height),
          forced: true,
          onChange: (newQuality: number) => {
            levels.forEach((level, levelIndex) => {
              if (level.height === newQuality) {
                hls.currentLevel = levelIndex
              }
            })
          },
        }
        setPlyrOptions({ ...plyrOptions, quality })
        hasQuality.current = true
      })
    },
    [hls, plyrOptions],
  )

  const { getVideoLanguage, updateVideoLanguage } = useVideoLanguage()

  const limitTime = useMemo(() => {
    if (!cookie) return undefined

    const policy = cookie
      .split('&')
      .find((part) => part.startsWith('Policy='))
      ?.split('=')[1]
    if (!policy) return undefined

    try {
      const decodedPolicy = Buffer.from(policy.slice(0, -1), 'base64').toString('utf-8') // policyの末尾の__を削除
      const limitTime = JSON.parse(decodedPolicy)['Statement'][0]['Condition']['DateLessThan'][
        'AWS:EpochTime'
      ] as number

      return limitTime
    } catch (e) {
      console.error('Failed to decode policy', e)
    }
  }, [cookie])

  // 毎秒cookieの有効期限が切れてないか確認
  useEffect(() => {
    const checkLimit = setInterval(() => {
      if (limitTime && Math.floor(Date.now() / 1000) > limitTime) {
        refetch().catch((e) => {
          console.error('Failed to refetch cookie', e)
        })
      }
    }, 1000)

    return () => clearInterval(checkLimit)
  })

  const usePlyr = useMemo(
    () => isHlsSupported && browserEnv.NEXT_PUBLIC_ENV === 'production',
    [isHlsSupported],
  )

  useEffect(() => {
    if (video?.fileUrl == undefined) return
    const videoElem = document.getElementById(videoElemId) as HTMLVideoElement
    const src = video.fileUrl ?? ''
    const q = cookie
    const usesHls = isHlsSupported && !isMobile
    const onMediaAttachedCallback = () => onMediaAttached(src)

    if (usesHls) {
      if (q !== undefined) {
        hls.config.xhrSetup = (xhr, url) => {
          xhr.open('GET', `${url}?${q}`, true)
        }
      }
      hls.detachMedia()
      hls.on(Hls.Events.MEDIA_ATTACHED, onMediaAttachedCallback)
      hls.attachMedia(videoElem)
    } else if (videoElem.canPlayType('application/vnd.apple.mpegurl')) {
      if (q !== undefined && lambda !== undefined) {
        const parsedUrl = new URL(src)
        const host = parsedUrl.hostname
        const pathComponents = parsedUrl.pathname.split('/')
        const id = pathComponents[2]
        const filename = pathComponents[3]
        videoElem.src = `${lambda}/${id}/${filename}?${q}&Origin-Host=${host}`
      } else {
        videoElem.src = src
      }
      videoElem.querySelectorAll('track')?.forEach((e) => {
        e.remove()
      })

      videoElem.setAttribute('playsInline', 'true')
      videoElem.load()
    } else {
      console.error(
        'This is an old browser that does not support MSE https://developer.mozilla.org/en-US/docs/Web/API/Media_Source_Extensions_API',
      )
    }

    const videoLanguage = getVideoLanguage(video.vtts ?? [])

    video.vtts?.forEach((subtitle, index) => {
      const id = `track-${subtitle.locale}`
      if (!document.getElementById(id)) {
        const elem = document.createElement('track') as HTMLTrackElement
        elem.id = id
        elem.kind = 'captions'
        const path = new URL(subtitle.url).pathname
        elem.src = `/proxy_resource${path}?v=${Date.now()}`
        elem.srclang = subtitle.locale
        elem.label = subtitle.label
        elem.default = subtitle.locale === videoLanguage
        if (usePlyr) {
          elem.oncuechange = () => {
            if (ref.current?.plyr.currentTrack !== undefined) {
              if (ref.current.plyr.currentTrack === -1) {
                updateVideoLanguage('disabled')
              }

              if (ref.current.plyr.currentTrack === index) {
                updateVideoLanguage(subtitle.locale)
              }
            }
          }
        }
        videoElem.appendChild(elem)
      }
    })

    if (!usePlyr) {
      let ignoreOnChange = true // 初期状態で何度かonChangeが発火するので無視する
      setTimeout(() => {
        Array.from(videoElem.textTracks).forEach((track) => {
          track.mode = track.language === videoLanguage ? 'showing' : 'hidden'
        })
        ;(ignoreOnChange = false), 1000
      })

      videoElem.textTracks.onchange = () => {
        if (ignoreOnChange) {
          return
        }

        const activeTrack = Array.from(videoElem.textTracks).find(
          (track) => track.mode === 'showing',
        )
        updateVideoLanguage(activeTrack?.language ?? 'disabled')
      }
    }

    addEventListeners?.(videoElem)

    return () => {
      if (usesHls) {
        hls.off(Hls.Events.MEDIA_ATTACHED, onMediaAttachedCallback)
      }
    }
  }, [
    video?.fileUrl,
    video?.vtts,
    cookie,
    lambda,
    isHlsSupported,
    usePlyr,
    onMediaAttached,
    getVideoLanguage,
    updateVideoLanguage,
    addEventListeners,
    hls,
  ])

  return usePlyr ? (
    <Plyr ref={ref} plyrOptions={plyrOptions} id={videoElemId} />
  ) : (
    <video id={videoElemId} style={{ width: '100%' }} controls autoPlay />
  )
}

Player.displayName = 'Player'

export default Player
